/* Hashtag helpers for the Post composer's '# Hashtags' panel (Part E, T31).
 *
 * A hashtag is '#' + 2–60 letters, numbers or underscores (any language),
 * with at least one letter. Matching is case-insensitive: '#JustListed' and
 * '#justlisted' are the same tag, so adding a set never doubles one up.
 *
 * The server applies the same rules (backend/services/hashtagMath.js);
 * keep the two in step.
 */

export const INSTAGRAM_LIMIT = 30;
// On LinkedIn and Facebook only the first few hashtags go out (setting, default on).
export const TRIM_KEEP = { linkedin: 3, facebook: 5 };

const BODY = /^[\p{L}\p{N}_]+$/u;
const HAS_LETTER = /\p{L}/u;
const IN_TEXT = /(^|[^\p{L}\p{N}_&#/])#([\p{L}\p{N}_]+)/gu;

export const tagKey = tag => String(tag).replace(/^#/, '').toLowerCase();

/** 'RealEstate' / '#RealEstate' → '#RealEstate'; anything that isn't a hashtag → null. */
export function cleanTag (raw) {
  if (typeof raw !== 'string') return null;
  const body = raw.trim().replace(/^#+/, '');
  if (body.length < 2 || body.length > 60) return null;
  if (!BODY.test(body) || !HAS_LETTER.test(body)) return null;
  return `#${body}`;
}

/** Every hashtag in the text, in order, as written (repeats kept). */
export function extractHashtags (text) {
  const out = [];
  for (const m of String(text || '').matchAll(IN_TEXT)) {
    const tag = cleanTag(m[2]);
    if (tag) out.push(tag);
  }
  return out;
}

/** The caption's hashtags, each once (first spelling wins). */
export function uniqueHashtags (text) {
  const seen = new Set();
  return extractHashtags(text).filter(t => (seen.has(tagKey(t)) ? false : seen.add(tagKey(t))));
}

/** How many different hashtags the caption has (what Instagram's 30 counts). */
export const countHashtags = text => uniqueHashtags(text).length;

const onlyHashtags = line => line.trim() !== '' && line.trim().split(/\s+/).every(w => !!cleanTag(w) && w.startsWith('#'));

/**
 * Adds tags to the end of the caption, skipping any it already has (any
 * case) and repeats in the list. They join the caption's last line when
 * that line is only hashtags, otherwise they start a new paragraph.
 * → { caption, added, skipped }
 */
export function addHashtags (caption, tags) {
  const text = String(caption || '');
  const have = new Set(extractHashtags(text).map(tagKey));
  const added = [];
  const skipped = [];
  for (const raw of Array.isArray(tags) ? tags : []) {
    const tag = cleanTag(raw);
    if (!tag) continue;
    if (have.has(tagKey(tag))) { skipped.push(tag); continue; }
    have.add(tagKey(tag));
    added.push(tag);
  }
  if (!added.length) return { caption: text, added, skipped };

  const base = text.replace(/\s+$/, '');
  const joined = added.join(' ');
  if (!base) return { caption: joined, added, skipped };
  const lastLine = base.slice(base.lastIndexOf('\n') + 1);
  return { caption: onlyHashtags(lastLine) ? `${base} ${joined}` : `${base}\n\n${joined}`, added, skipped };
}

/** Keeps the first `keep` different hashtags and removes the rest, tidying the spaces they leave. */
export function trimHashtags (caption, keep) {
  const text = String(caption || '');
  const kept = new Set();
  let removed = 0;
  const out = text.replace(IN_TEXT, (match, before, body) => {
    const tag = cleanTag(body);
    if (!tag) return match;
    const k = tagKey(tag);
    if (kept.has(k)) return match;
    if (kept.size < keep) { kept.add(k); return match; }
    removed++;
    return before;
  });
  if (!removed) return text;
  return out
    .split('\n')
    .map(line => line.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** The caption a network gets: on LinkedIn / Facebook, trimmed unless the user switched it off. */
export function captionForNetwork (caption, platform, settings) {
  const keep = TRIM_KEEP[platform];
  if (!keep || (settings && settings.trimLinkedInFacebook === false)) return caption;
  return trimHashtags(caption, keep);
}
